import { ADMIN_FUNNEL_COLORS } from '@/config/adminChartTheme';
import React, { useEffect, useState } from 'react';
import ChartCard from '../ChartCard';
import { fetchSubmissionsStats } from '../../../services/campaignAnalyticsService';
import { TimeInterval } from '../../../types/types';

interface SubmissionsStats {
  allTime: {
    total: number;
    pending: number;
    approved: number;
    rejected: number;
    aiFlagged: number;
    aiApprovalRate: number;
    avgValidationTimeHours: number | null;
  };
  period: { submissions: { value: number; change: number } };
  platformBreakdown: Record<string, number>;
  contentTypeBreakdown: Record<string, number>;
}

interface SubmissionFunnelChartProps {
  filter: TimeInterval;
  refreshToken?: number;
}

const FunnelBar: React.FC<{
  label: string;
  value: number;
  total: number;
  color: string;
}> = ({ label, value, total, color }) => {
  const pct = total > 0 ? Math.round((value / total) * 100) : 0;
  return (
    <div className="flex items-center gap-3">
      <span className="text-xs text-[#9B919D] w-20 shrink-0 text-right">
        {label}
      </span>
      <div className="flex-1 bg-[#F5F5F5] rounded-full h-2.5 overflow-hidden">
        <div
          className="h-full rounded-full transition-all duration-700"
          style={{ width: `${pct}%`, backgroundColor: color }}
        />
      </div>
      <span className="text-xs font-semibold text-[#172B4D] w-12 shrink-0">
        {value.toLocaleString()}
      </span>
      <span className="text-xs text-[#9B919D] w-8 shrink-0">{pct}%</span>
    </div>
  );
};

const SubmissionFunnelChart: React.FC<SubmissionFunnelChartProps> = ({
  filter,
  refreshToken,
}) => {
  const [data, setData] = useState<SubmissionsStats | null>(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    setLoading(true);
    fetchSubmissionsStats(filter)
      .then(setData)
      .finally(() => setLoading(false));
  }, [filter, refreshToken]);

  const total = data?.allTime.total ?? 0;

  return (
    <ChartCard
      title="Submission Funnel"
      subtitle="All-time content validation pipeline"
      loading={loading}
      fillHeight
      className="h-full w-full min-h-0"
      actions={
        data?.allTime.avgValidationTimeHours != null ? (
          <div className="text-right">
            <p className="text-xs text-[#9B919D]">Avg. validation</p>
            <p className="text-sm font-bold text-[#172B4D]">
              {data.allTime.avgValidationTimeHours}h
            </p>
          </div>
        ) : undefined
      }
    >
      <div className="flex flex-col flex-1 min-h-0">
        <div className="space-y-3 shrink-0">
          <FunnelBar
            label="Total"
            value={total}
            total={total}
            color={ADMIN_FUNNEL_COLORS.total}
          />
          <FunnelBar
            label="Approved"
            value={data?.allTime.approved ?? 0}
            total={total}
            color={ADMIN_FUNNEL_COLORS.approved}
          />
          <FunnelBar
            label="Pending"
            value={data?.allTime.pending ?? 0}
            total={total}
            color={ADMIN_FUNNEL_COLORS.pending}
          />
          <FunnelBar
            label="AI Flagged"
            value={data?.allTime.aiFlagged ?? 0}
            total={total}
            color={ADMIN_FUNNEL_COLORS.aiFlagged}
          />
          <FunnelBar
            label="Rejected"
            value={data?.allTime.rejected ?? 0}
            total={total}
            color={ADMIN_FUNNEL_COLORS.rejected}
          />
        </div>
        <div className="flex-1 min-h-0 min-w-0" aria-hidden />
      </div>

      {data && (
        <div className="shrink-0 mt-6 pt-4 border-t border-[#F0F0F0] grid grid-cols-2 gap-4">
          <div>
            <p className="text-xs text-[#9B919D] mb-1">Platform</p>
            <div className="space-y-1">
              {Object.entries(data.platformBreakdown).map(
                ([platform, count]) => (
                  <div key={platform} className="flex justify-between text-xs">
                    <span className="text-[#6B778C]">{platform}</span>
                    <span className="font-semibold text-[#172B4D]">
                      {count.toLocaleString()}
                    </span>
                  </div>
                ),
              )}
            </div>
          </div>
          <div>
            <p className="text-xs text-[#9B919D] mb-1">Content Type</p>
            <div className="space-y-1">
              {Object.entries(data.contentTypeBreakdown).map(
                ([type, count]) => (
                  <div key={type} className="flex justify-between text-xs">
                    <span className="text-[#6B778C] capitalize">{type}</span>
                    <span className="font-semibold text-[#172B4D]">
                      {count.toLocaleString()}
                    </span>
                  </div>
                ),
              )}
            </div>
          </div>
        </div>
      )}
    </ChartCard>
  );
};

export default SubmissionFunnelChart;
